import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, MessageCircle } from 'lucide-react';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { FeatureFlag, useFeatureFlag } from '@/services/featureFlag.service';
import { shortid } from '@/services/id.service';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useLogout } from '@/hooks/useLogout';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import OpenRequests from '@/components/home/OpenRequests';
import KnowledgeBaseAttention from '@/components/home/KnowledgeBaseAttention';

/**
 * The mobile home screen.
 *
 * Reorganized rather than restyled: on a phone the page is a queue, not a
 * dashboard. Ask Ellie sits at the top because it's the one thing you start
 * from scratch; below it, the two things that might be waiting on you — open
 * requests and pending knowledge-base changes — stack in that order.
 *
 * The desktop home's source-inventory card ("What Ellie can see") is dropped.
 * It's reference material you consult while building a model, which is not
 * something anyone does from a phone.
 */

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function MobileHome() {
  const navigate = useNavigate();
  const { user } = useAppAuth();
  const { canViewDataManagement } = useUserRoles();
  const isAiAssistantEnabled = useFeatureFlag(FeatureFlag.AI_ASSISTANT, false);
  const useCasesEnabled = useFeatureFlag(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);
  const { data: tenantConfig } = useTenantConfig();
  const logout = useLogout();
  // Follows the theme: the hero gradient behind it is light in light mode.
  const brandLogo = useBrandLogo();
  const [question, setQuestion] = useState('');

  const firstName = (user?.name || '').split(' ')[0];

  function ask() {
    const prompt = question.trim();
    if (!prompt) return;
    // Same handoff as the desktop ask box: a fresh thread with its own URL, the
    // question passed on history state so a refresh doesn't re-ask it.
    navigate(`/ellie/conversation/${shortid()}`, { state: { initialQuestion: prompt } });
  }

  return (
    <div className="h-full overflow-y-auto bg-page-bg">
      <header className="px-4 pt-5 pb-5 bg-gradient-to-br from-hero-start via-hero-mid to-hero-end text-foreground">
        {/* The only place the mobile app is branded — there's no sidebar
            header to carry it, and the tab bar has no room for a wordmark. */}
        <img src={brandLogo} alt="Pliable" className="h-7 w-auto mb-4" />

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold leading-tight">
              {greeting()}
              {firstName ? `, ${firstName}` : ''}
            </h1>
            {(tenantConfig as any)?.tenant_name && (
              <p className="text-sm text-muted-foreground mt-0.5 truncate">
                {(tenantConfig as any).tenant_name}
              </p>
            )}
          </div>
          {/* No sidebar here, so the only way out lives on the home screen. */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Log out"
            className="flex-shrink-0 -mr-1 text-muted-foreground"
            onClick={logout}
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>

        {isAiAssistantEnabled && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-card/70 p-1.5 pl-3">
            <MessageCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <Input
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') ask();
              }}
              placeholder="Ask Ellie"
              // enterKeyHint turns the phone keyboard's return key into "Send".
              enterKeyHint="send"
              className="border-0 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-0 shadow-none px-0"
            />
            <Button size="sm" onClick={ask} className="flex-shrink-0">
              Ask
            </Button>
          </div>
        )}
      </header>

      {canViewDataManagement && (
        <div className="p-4 flex flex-col gap-4">
          <OpenRequests />
          {useCasesEnabled && <KnowledgeBaseAttention />}
        </div>
      )}
    </div>
  );
}
